/**
 * This file was auto-generated by `scripts/codegen-events.mjs` from
 * `../quest-v2-api/events.schema.json`. Do not make direct changes to the file — change the
 * API's Pydantic models and re-run `pnpm codegen`.
 *
 * Payloads for POST /stories/{story_id}/turns. One event per SSE frame, keyed by the frame's
 * `event:` name. A `: ping` comment arrives every 15s and carries no payload. Exactly one
 * terminal event ends every stream.
 */

/**
 * Shared with the REST surface, therefore imported rather than restated: the stream and
 * `openapi.json` describe the same game, and a second definition of `Change` is a second thing
 * to get wrong. Re-exported so a consumer needs one import.
 */
import type {
  Change,
  CharacterSnapshot,
  ErrorDetail,
  GameTime,
  Mention,
} from '@/api/generated';

export type { Change, CharacterSnapshot, ErrorDetail, GameTime, Mention };

/**
 * Terminal, success — and it carries the post-`settle()` state (P1).
 *
 * Without the snapshot every turn needs a follow-up `GET /stories/{id}/state`, on the one
 * path where latency is already the weak point, and until it arrives the character panel
 * disagrees with the narration the player is reading.
 *
 * The full world stays behind `GET /stories/{id}/state`; only `world.time` rides along,
 * because the dateline header renders it every chapter.
 */
export interface ChapterEvent {
  chapter_number: number;
  action: string | null;
  outcome: string;
  situation: string;
  narration: string;
  choices: string[];
  changes: Change[];
  /**
   * U6 — the same spans the stored chapter carries. They arrive only here, on the terminal
   * event: the streamed scene is plain prose, and the links activate when the chapter commits
   * (`docs/engine/narration.md`).
   */
  mentions: Mention[];
  is_death: boolean;
  cause_of_death: string | null;
  /**
   * The server's timestamp for the committed chapter. Present so the client's commit into its
   * cache writes a real value instead of fabricating one.
   */
  created_at: string;
  /**
   * How long the action took in game time. The same number the `time_spent` change carries; it
   * rides here too so the client's cache commit writes the row it would have read back, rather
   * than a zero it would have to unlearn on the next fetch.
   */
  duration_seconds: number;
  character: CharacterSnapshot;
  world: WorldTime;
}

/** Arrives *before* `chapter`, never instead of it. The story still gets its final scene. */
export interface Death {
  cause_of_death: string;
}

/** Phase B token deltas, appended in order. */
export interface NarrationDelta {
  text: string;
}

/** Phase transitions. `label` is in the game's voice and is what the feed shows. */
export interface Progress {
  /** `resolution` or `narration` today. Treat unknown values as fine. */
  phase: string;
  /** Ready to render, in the story's language. Never a key — see `ui.turn.phase.*`. */
  label: string;
}

/** Terminal, failure — the same envelope object every non-2xx response carries. */
export interface TurnError {
  error: ErrorDetail;
}

/** Immediately, before any LLM call — the earliest proof the server has the turn. */
export interface TurnStarted {
  chapter_number: number;
}

/**
 * The world half of the `chapter` event's snapshot: time, and nothing else.
 *
 * The client's dateline header renders it every chapter; locations, NPCs and factions are
 * worth
 * a round trip only when the player opens the panel.
 */
export interface WorldTime {
  time: GameTime;
}

/**
 * Every published event name, in the order the API lists them. `satisfies` is what makes an
 * event the backend adds a compile error at every exhaustive switch.
 */
export const TURN_EVENT_NAMES = [
  'turn.started',
  'progress',
  'change',
  'narration.delta',
  'death',
  'chapter',
  'error',
] as const;

/** Exactly one of these ends every stream. */
export const TERMINAL_TURN_EVENT_NAMES = [
  'chapter',
  'error',
] as const satisfies readonly TurnEventName[];

/** The payload carried by each event name, keyed by the frame’s `event:` field. */
export interface TurnEventPayloads {
  'turn.started': TurnStarted;
  progress: Progress;
  change: Change;
  'narration.delta': NarrationDelta;
  death: Death;
  chapter: ChapterEvent;
  error: TurnError;
}

export type TurnEventName = (typeof TURN_EVENT_NAMES)[number];

export type TerminalTurnEventName = (typeof TERMINAL_TURN_EVENT_NAMES)[number];

/**
 * One parsed SSE frame, as a discriminated union over `event`. Narrow with `event.event ===
 * 'chapter'` and `event.data` follows.
 */
export type TurnEvent = {
  [Name in TurnEventName]: { event: Name; data: TurnEventPayloads[Name] };
}[TurnEventName];

/** Narrowing helper: `Extract<TurnEvent, {event: 'chapter'}>['data']`, spelled readably. */
export type TurnEventData<Name extends TurnEventName> = TurnEventPayloads[Name];

/** A frame whose arrival means the stream is over. */
export type TerminalTurnEvent = Extract<TurnEvent, { event: TerminalTurnEventName }>;
